import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { UseMutationOptions, useQueryClient } from 'react-query';
import http from '../config/http-service';
import { restaurantInquiriesEndpoint } from './use-create-restaurant-inquiry-api';

// DELETE api/v1/cms/restaurant-inquiries/:id — soft delete of the inquiry and
// its items. Response is just { data: { id } }, not the full object. There is
// no restore endpoint, so the UI must treat this as irreversible (confirm first).
type TData = { id: number };
type TError = AxiosError;
type TVariables = { id: number | string };

type Options = Omit<UseMutationOptions<TData, TError, TVariables>, 'mutationFn'>;
type TReturnType = ReturnType<typeof useMutationWrapper<TData, TError, TVariables>>;

export function useDeleteRestaurantInquiryApi(options?: Options): TReturnType {
  const queryClient = useQueryClient();
  const { onSuccess: userOnSuccess, ...restOptions } = options || {};

  const mutationFn = async ({ id }: TVariables): Promise<TData> => {
    const response = await http.delete(`${restaurantInquiriesEndpoint}/${id}`);
    return response.data?.data ?? response.data;
  };

  return useMutationWrapper(mutationFn, {
    mutationKey: [restaurantInquiriesEndpoint, 'delete'],
    onSuccess: (data, variables, context) => {
      // Drop the deleted inquiry's detail from the cache entirely (not just
      // invalidate): react-query keeps stale data when a refetch errors, so an
      // invalidated-but-cached detail would keep rendering the deleted inquiry
      // after its refetch 404s.
      queryClient.removeQueries([restaurantInquiriesEndpoint, 'detail', variables.id]);
      queryClient.invalidateQueries([restaurantInquiriesEndpoint]);
      userOnSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
}
